(() => {
  const money = (cents) => {
    const currency = window.Shopify && Shopify.currency && Shopify.currency.active
      ? Shopify.currency.active
      : 'EUR';

    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency
    }).format((Number(cents) || 0) / 100);
  };

  const initEnterpriseQuickView = (root) => {
    if (!root || root.dataset.enterpriseQvReady === 'true') return;
    root.dataset.enterpriseQvReady = 'true';

    const variantsNode = root.querySelector('[data-qv-variants]');
    if (!variantsNode) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsNode.textContent || '[]');
    } catch (error) {
      return;
    }

    const groups = Array.from(root.querySelectorAll('[data-qv-option-group]'));
    const selected = groups.map((group) => {
      const active = group.querySelector('[data-qv-option].is-selected');
      return active ? active.dataset.value : '';
    });

    const variantInput = root.querySelector('[data-qv-variant-id]');
    const price = root.querySelector('[data-qv-price]');
    const compare = root.querySelector('[data-qv-compare]');
    const stock = root.querySelector('[data-qv-stock]');
    const stockDot = root.querySelector('[data-qv-stock-dot]');
    const image = root.querySelector('.enterprise-qv__image');
    const add = root.querySelector('[data-qv-add]');
    const addLabel = root.querySelector('[data-qv-add-label]');
    const message = root.querySelector('[data-qv-message]');

    const findVariant = () => {
      return variants.find((variant) =>
        variant.options.every((value, index) => value === selected[index])
      );
    };

    const updateAvailability = () => {
      groups.forEach((group, groupIndex) => {
        group.querySelectorAll('[data-qv-option]').forEach((button) => {
          const candidate = button.dataset.value;

          const possible = variants.some((variant) => {
            if (!variant.available) return false;

            return variant.options.every((value, index) => {
              if (index === groupIndex) return value === candidate;
              if (!selected[index]) return true;
              return value === selected[index];
            });
          });

          button.disabled = !possible;
          button.classList.toggle('is-unavailable', !possible);
        });
      });
    };

    const applyVariant = () => {
      const variant = findVariant();
      updateAvailability();

      if (!variant) {
        if (add) add.disabled = true;
        if (stock) stock.textContent = 'Auswahl nicht verfügbar';
        if (stockDot) stockDot.classList.add('is-off');
        return;
      }

      if (variantInput) variantInput.value = variant.id;
      if (price) price.textContent = money(variant.price);

      if (compare) {
        if (variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)) {
          compare.textContent = money(variant.compare_at_price);
          compare.classList.remove('is-hidden');
        } else {
          compare.textContent = '';
          compare.classList.add('is-hidden');
        }
      }

      if (stock) stock.textContent = variant.available ? 'Auf Lager' : 'Nicht verfügbar';
      if (stockDot) stockDot.classList.toggle('is-off', !variant.available);
      if (add) add.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? 'In den Warenkorb' : 'Nicht verfügbar';

      if (variant.featured_image && variant.featured_image.src && image) {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = variant.featured_image.src;
      }

      const url = new URL(root.dataset.productUrl || window.location.href, window.location.origin);
      url.searchParams.set('variant', variant.id);
      root.querySelectorAll('a[href]').forEach((link) => {
        if (link.classList.contains('enterprise-qv__details') || link.closest('.enterprise-qv__title')) {
          link.href = url.pathname + url.search;
        }
      });
    };

    root.addEventListener('click', (event) => {
      const option = event.target.closest('[data-qv-option]');
      if (option && !option.disabled) {
        const index = Number(option.dataset.qvOption);
        selected[index] = option.dataset.value;

        root.querySelectorAll('[data-qv-option="' + index + '"]').forEach((button) => {
          const active = button === option;
          button.classList.toggle('is-selected', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        if (message) message.textContent = '';
        applyVariant();
        return;
      }

      if (event.target.closest('[data-qv-minus]') || event.target.closest('[data-qv-plus]')) {
        const input = root.querySelector('[data-qv-qty]');
        if (!input) return;
        const change = event.target.closest('[data-qv-plus]') ? 1 : -1;
        input.value = Math.max(1, Number(input.value || 1) + change);
      }
    });

    const form = root.querySelector('[data-qv-form]');
    if (form) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (!variantInput || !variantInput.value || !add || add.disabled) return;

        add.disabled = true;
        if (message) message.textContent = 'Wird hinzugefügt …';

        try {
          const base = window.Shopify && Shopify.routes && Shopify.routes.root
            ? Shopify.routes.root
            : '/';

          const response = await fetch(base + 'cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json'
            },
            body: new FormData(form)
          });

          if (!response.ok) throw new Error('cart add failed');

          if (message) message.textContent = 'Zum Warenkorb hinzugefügt.';
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

          if (window.MinimogEvents && MinimogTheme.pubSubEvents && MinimogTheme.pubSubEvents.cartUpdate) {
            window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { source: 'enterprise-ui' });
          }
        } catch (error) {
          if (message) message.textContent = 'Bitte erneut versuchen.';
          add.disabled = false;
        }
      });
    }

    updateAvailability();
    applyVariant();
  };

  class QuickView {
    constructor() {
      this.modal = new MinimogTheme.Modal();
      this.isOpen = false;

      addEventDelegate({
        selector: '.m-product-quickview-button',
        handler: (event, target) => {
          event.preventDefault();
          this.target = target;
          this.toggleLoading(target, true);

          const handle = target.dataset.productHandle;
          if (handle) this.fetchHtml(handle);
        }
      });

      if (window.MinimogEvents && MinimogTheme.pubSubEvents) {
        window.MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.cartUpdate,
          () => {
            if (this.modal && this.isOpen) this.close();
          }
        );
      }
    }

    fetchHtml(handle) {
      fetchSection('product-quickview', {
        url: window.MinimogSettings.base_url + 'products/' + handle
      })
        .then((documentNode) => {
          const content = documentNode.querySelector('.m-product-quickview');
          if (!content) throw new Error('Quick view content missing');

          this.modal.appendChild(content);
          this.modal.setWidth('940px');
          this.modal.open();
          initEnterpriseQuickView(content);

          this.toggleLoading(this.target, false);
          this.isOpen = true;
        })
        .catch(() => {
          this.toggleLoading(this.target, false);
        });
    }

    close(event) {
      this.modal.close(event);
      this.isOpen = false;
    }

    toggleLoading(target, active) {
      if (!target) return;
      target.classList.toggle('m-spinner-loading', active);
    }
  }

  MinimogTheme.ProductQuickView = new QuickView();
})();